import { Fragment } from "react";
import { TriangleAlert } from "lucide-react";
import { OverlayTrigger, Tooltip as BootstrapTooltip } from "react-bootstrap";

import CustomTooltip from "@/components/desktop/tooltip/CustomTooltip";
import useFilterStore from "@/stores/useFilterStore";
import { formatNumber } from "@/utils/numberFormat";
import { normalizePercent } from "@/pages/Planning/utils/normalizePercent";
import { getUnitLabel } from "@/utils/unitFormatter";

import styles from "./OATooltip.module.scss";

const ACTIVE_OPTIONALS_TOOLTIP = "Se están usando productos opcionales; el consumo estimado de cada uno se verá reflejado al terminar la aplicación.";

const normalizeQuantity = (value) => {
	const asNumber = Number(String(value ?? 0).replace(",", "."));
	if (Number.isNaN(asNumber) || asNumber < 0) {
		return 0;
	}
	return asNumber;
};

const ActiveOptionalsAlert = () => (
	<OverlayTrigger overlay={<BootstrapTooltip>{ACTIVE_OPTIONALS_TOOLTIP}</BootstrapTooltip>}>
		<span className={styles.activeOptionalsAlert} aria-label="Consumo con opcionales activos">
			<TriangleAlert size={14} color="#d80f0f" />
		</span>
	</OverlayTrigger>
);

const OATooltip = ({ tooltip, applicationOrdersStateId = 0, rowIndex = 0, totalRows = 0, children }) => {
	const tooltipDetails = Array.isArray(tooltip?.details) ? tooltip.details : [];
	if (tooltipDetails.length === 0) {
		return <>{children}</>;
	}

	const globalUnitMeasurement = useFilterStore((state) => state.filter?.unit_measurement || "kg_l");
	const orderStatusFinished = Number(applicationOrdersStateId) === 5;
	const percentUsed = normalizePercent(tooltip?.head?.percent_used);
	const percentPending = normalizePercent(tooltip?.head?.percent_pending);
	const isBottomRow = totalRows > 0 && rowIndex >= Math.floor(totalRows / 2);

	const renderQuantityCell = (detailRow, value) => {
		if (detailRow?.has_active_optionals && !orderStatusFinished) {
			return <ActiveOptionalsAlert />;
		}
		const unitLabel = getUnitLabel(detailRow?.unit_measurement, globalUnitMeasurement);
		return (
			<>
				{formatNumber(normalizeQuantity(value))} {unitLabel}
			</>
		);
	};

	return (
		<div className={styles.tooltipWrapper}>
			{children}
			<div className={`${styles.tooltipPanel} ${isBottomRow ? styles.tooltipPanelTop : ""}`}>
				<div className={styles.tooltipHead}>
					<div className={styles.tooltipPercent}>
						<span className={styles.percentLabel}>Pendiente</span>
						<span className={`${styles.percentValue} ${percentPending === 0 ? styles.percentValueGreen : ""}`}>
							{formatNumber(percentPending)}%
						</span>
					</div>
					<div className={styles.tooltipPercent}>
						<span className={styles.percentLabel}>Utilizado</span>
						<span className={`${styles.percentValue} ${percentUsed >= 100 ? styles.percentValueGreen : ""}`}>{formatNumber(percentUsed)}%</span>
					</div>
				</div>
				<table className={styles.tooltipTable}>
					<thead>
						<tr>
							<th className={styles.tableHeader}>Producto</th>
							<th className={`${styles.tableHeader} ${styles.tableHeaderNumeric}`}>Cant. Planificada</th>
							<th className={`${styles.tableHeader} ${styles.tableHeaderNumeric}`}>Cant. En Uso</th>
							<th className={`${styles.tableHeader} ${styles.tableHeaderNumeric}`}>Cant. Pendiente</th>
							<th className={`${styles.tableHeader} ${styles.tableHeaderNumeric}`}>Cant. Utilizada</th>
						</tr>
					</thead>
					<tbody>
						{tooltipDetails.map((detailRow, detailIndex) => (
							<Fragment key={`detail-${detailIndex}`}>
								<tr className={styles.tableRow}>
									<td className={styles.tableCell}>
										<CustomTooltip text={detailRow.product || "-"} />
									</td>
									<td className={`${styles.tableCell} ${styles.tableCellNumeric}`}>{renderQuantityCell(detailRow, detailRow.quantity)}</td>
									<td className={`${styles.tableCell} ${styles.tableCellNumeric}`}>
										{formatNumber(normalizeQuantity(detailRow.total_out))} {getUnitLabel(detailRow.unit_measurement, globalUnitMeasurement)}
									</td>
									<td className={`${styles.tableCell} ${styles.tableCellNumeric}`}>
										{renderQuantityCell(detailRow, detailRow.total_pending)}
									</td>
									<td className={`${styles.tableCell} ${styles.tableCellNumeric}`}>{renderQuantityCell(detailRow, detailRow.total_used)}</td>
								</tr>
								{(detailRow.optional_products || []).map((optionalRow, optionalIndex) => (
									<tr key={`detail-${detailIndex}-optional-${optionalIndex}`} className={styles.optionalRow}>
										<td className={styles.tableCell}>
											<span className={styles.optionalLine}>
												<span className={styles.optionPrefix}>o</span>
												{optionalRow.product || "-"}
											</span>
										</td>
										<td className={`${styles.tableCell} ${styles.tableCellNumeric}`}>
											{renderQuantityCell(detailRow, optionalRow.quantity)}
										</td>
										<td className={`${styles.tableCell} ${styles.tableCellNumeric}`}>
											{formatNumber(normalizeQuantity(optionalRow.total_out))}{" "}
											{getUnitLabel(optionalRow.unit_measurement, globalUnitMeasurement)}
										</td>
										<td className={`${styles.tableCell} ${styles.tableCellNumeric}`}>
											{renderQuantityCell(detailRow, optionalRow.total_pending)}
										</td>
										<td className={`${styles.tableCell} ${styles.tableCellNumeric}`}>
											{renderQuantityCell(detailRow, optionalRow.total_used)}
										</td>
									</tr>
								))}
							</Fragment>
						))}
					</tbody>
				</table>
			</div>
		</div>
	);
};

export default OATooltip;
